import { AccessControlCode } from '@shared/constants';
import { UserRole } from '@shared/types';
import {
  Bell,
  CreditCard,
  LucideIcon,
  Printer,
  ReceiptText,
  Store,
  Users,
} from 'lucide-react';
import type { SidebarLinkConfig } from '@components/dashboard/superuser/sidebarConfig';

/**
 * Each row maps to one data source in useSettingsDirectory. `outlet` rows
 * need a concrete outlet; `company` rows also work in the "All outlets"
 * scope.
 */
export type DirectorySource =
  | 'outletProfile'
  | 'sections'
  | 'staff'
  | 'roles'
  | 'devices'
  | 'printers'
  | 'ebmDevice'
  | 'fiscalItems'
  | 'saleSubmissions'
  | 'email'
  | 'subscription';

export interface DirectoryRowConfig extends Pick<
  SidebarLinkConfig,
  'requiredControl' | 'allowedRoles' | 'requiresFiscalIntegration'
> {
  id: DirectorySource;
  label: string;
  to: string;
  scope: 'outlet' | 'company';
  /** Extra words the find bar matches, beyond label and summary. */
  keywords: string[];
}

export interface DirectoryGroupConfig {
  id: string;
  title: string;
  icon: LucideIcon;
  rows: DirectoryRowConfig[];
}

// Access requirements mirror each destination route's own gate in
// routes/settingsRoutes.tsx and routes/dashboardRoutes.tsx, so a row is
// never shown for a page that would then deny access.
export const SETTINGS_DIRECTORY: DirectoryGroupConfig[] = [
  {
    id: 'outlet',
    title: 'Outlet & receipts',
    icon: Store,
    rows: [
      {
        id: 'outletProfile',
        label: 'Outlet profile',
        to: '/dashboard/outlets',
        scope: 'outlet',
        requiredControl: AccessControlCode.DASHBOARD_PAGE_ACCESS,
        keywords: ['tin', 'phone', 'address', 'footnote', 'receipt', 'name'],
      },
      {
        id: 'sections',
        label: 'Sections & tables',
        to: '/dashboard/sections-tables',
        scope: 'outlet',
        requiredControl: AccessControlCode.DASHBOARD_PAGE_ACCESS,
        keywords: ['floor', 'layout', 'dine', 'table', 'section'],
      },
    ],
  },
  {
    id: 'people',
    title: 'People & access',
    icon: Users,
    rows: [
      {
        id: 'staff',
        label: 'Staff accounts',
        to: '/settings/users',
        scope: 'company',
        requiredControl: AccessControlCode.SETTINGS_USERS_MANAGE,
        keywords: ['users', 'employees', 'passcode', 'pin', 'seats', 'invite'],
      },
      {
        id: 'roles',
        label: 'Roles & policies',
        to: '/settings/roles',
        scope: 'company',
        requiredControl: AccessControlCode.SETTINGS_ROLES_MANAGE,
        keywords: ['permissions', 'policy', 'access', 'cashier', 'waiter'],
      },
      {
        id: 'devices',
        label: 'Registered devices',
        to: '/settings/security/registered-devices',
        scope: 'company',
        allowedRoles: [UserRole.SUPER, UserRole.SUPER_ADMIN],
        requiredControl:
          AccessControlCode.SETTINGS_SECURITY_REGISTERED_DEVICES_MANAGE,
        keywords: ['security', 'terminal', 'tablet', 'block', 'login'],
      },
    ],
  },
  {
    id: 'hardware',
    title: 'Hardware',
    icon: Printer,
    rows: [
      {
        id: 'printers',
        label: 'Printers & routing',
        to: '/settings/printers/all',
        scope: 'outlet',
        requiredControl: AccessControlCode.DASHBOARD_SECTIONS_TABLES_MANAGE,
        keywords: ['kot', 'bill', 'kitchen', 'ticket', 'receipt', 'thermal'],
      },
    ],
  },
  {
    id: 'fiscal',
    title: 'Fiscal · RRA EBM',
    icon: ReceiptText,
    rows: [
      {
        id: 'ebmDevice',
        label: 'EBM device',
        to: '/settings/ebm/configuration',
        scope: 'outlet',
        requiresFiscalIntegration: true,
        requiredControl: AccessControlCode.SETTINGS_EBM_CONFIG_MANAGE,
        keywords: ['vsdc', 'rra', 'fiscal', 'tin', 'activation', 'health'],
      },
      {
        id: 'fiscalItems',
        label: 'Menu item tax setup',
        to: '/settings/ebm/missing-fiscal-setup',
        scope: 'company',
        requiresFiscalIntegration: true,
        requiredControl: AccessControlCode.SETTINGS_EBM_FISCAL_SETUP_MANAGE,
        keywords: ['vat', 'tax', 'item code', 'classification', 'menu'],
      },
      {
        id: 'saleSubmissions',
        label: 'Sale submissions',
        to: '/settings/ebm/sale-records',
        scope: 'company',
        requiresFiscalIntegration: true,
        requiredControl: AccessControlCode.SETTINGS_EBM_SALE_RECORDS_MANAGE,
        keywords: ['invoice', 'failed', 'rra', 'receipt', 'resubmit'],
      },
    ],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    icon: Bell,
    rows: [
      {
        id: 'email',
        label: 'Report emails',
        to: '/settings/email-configurations',
        scope: 'outlet',
        requiredControl: AccessControlCode.SETTINGS_PAGE_ACCESS,
        keywords: ['email', 'recipients', 'sales report', 'stock report'],
      },
    ],
  },
  {
    id: 'plan',
    title: 'Plan & billing',
    icon: CreditCard,
    rows: [
      {
        id: 'subscription',
        label: 'Subscription',
        to: '/settings/billing',
        scope: 'company',
        requiredControl: AccessControlCode.DASHBOARD_BILLINGS_ACCESS,
        keywords: ['billing', 'plan', 'invoice', 'payment', 'card', 'renew'],
      },
    ],
  },
];
